import React, {useCallback, useMemo, useState} from 'react';
import {Autocomplete, Box, IconButton, InputAdornment, Stack, TextField, Typography} from '@mui/material';
// Assicurati che l'import punti alla tua interfaccia aggiornata
import {EditorialAsset} from "@/types/editorialAssetsTypes";
import EditorialAssetCard from "@/pages/custom-components/CommonComponents/EditorialAssetCard";
import EditorialAssetForm from "@/pages/custom-components/EditorAssetComponents/EditorAssetForm";
import {useEditorialAssets} from "@/contexts/EditorialAssetsContext";
import {useSelectedCard} from '@/contexts/SelectedCardContext';
import {DEFAULT_ASPECT_RATIO, L_CUT_OFFSET} from "@/constants";
import {isReplaySegment, applyLCutOffsetsToOverlays} from "@/utils/editor/editorUtils";
import {AspectRatio} from "@/reactvideoeditor/pro/types";
import {findClosestAspectRatio} from "@/utils/generic";
import {
    ASPECT_RATIO_OPTIONS
} from "@/reactvideoeditor/pro/components/advanced-timeline/components/timeline-header/aspect-ratio-dropdown";
import Collapse from "@mui/material/Collapse";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {OverlayType} from 'reactvideoeditor/pro/types';
import {useForm} from "react-hook-form";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {EDITORIAL_ASSETS, EditorialAssetRenderParameters} from 'types/editorialAssetsTypes';
import {AGGREGATION_USAGE_TYPE} from "@/pages/extra-pages/ContentAggregation";
import {DASHED_SEPARATOR_STYLE} from "@/constants/styles";
import {useMaterializationSettings} from "@/contexts/MaterializationSettingsContext";

export interface EditorialAssetListProps {
    editorialAssets: EditorialAsset[];
    selectedEditorialAssets: EditorialAsset[];
    onAssetSelection: (asset: EditorialAsset) => void;
    onAssetDeselect?: (asset: EditorialAsset) => void;
    layout?: 'vertical' | 'horizontal';
    displayForm?: boolean;
    aspectRatio?: AspectRatio;
    overlays?: any
    usageMode?: AGGREGATION_USAGE_TYPE
    isEditorDrawer?: boolean
    // Necessario per applicare in timeline gli offset L-Cut al click del save icon (oltre che su Apply)
    onOverlaysChange?: (overlays: any[]) => void
}

interface AssetSelectorProps {
    placementLabel: string;
    assets: EditorialAsset[];
    selectedAssets: EditorialAsset[];
    onAssetSelection: (asset: EditorialAsset) => void;
    onAssetDeselect?: (asset: EditorialAsset) => void;
    displayForm?: boolean;
    aspectRatio?: AspectRatio;
    overlays?: any
    disabled?: boolean;
    simplified?: boolean;
    onOverlaysChange?: (overlays: any[]) => void
}

const ASPECT_RATIO_LABELS = ASPECT_RATIO_OPTIONS.map((ratio: any) => ratio.label);

const AssetSelector: React.FC<AssetSelectorProps> = React.memo(({
                                                         placementLabel,
                                                         assets,
                                                         selectedAssets,
                                                         onAssetSelection,
                                                         onAssetDeselect,
                                                         displayForm = false,
                                                         aspectRatio = DEFAULT_ASPECT_RATIO,
                                                         overlays = [],
                                                         disabled = false,
                                                         simplified = false,
                                                         onOverlaysChange
                                                     }) => {
    const {changeAssetRenderParameter, assetRenderParametersMap} = useEditorialAssets();
    const {lCutOffsets, setLCutOffset} = useMaterializationSettings();
    const [isInterleaveEditing, setIsInterleaveEditing] = useState(false);
    const defaultValues = {number: 1}
    const { reset } = useForm({
        defaultValues: defaultValues,
        mode: "onChange",
    });

    const handleInterleaveToggleEdit = useCallback(() => {
        if (isInterleaveEditing) {
            reset(defaultValues);
        }
        setIsInterleaveEditing(prev => !prev);
    }, [isInterleaveEditing, reset]);

    // Stato per gestire l'apertura/chiusura del form
    const [isFormOpen, setIsFormOpen] = useState(false);

    // Stato per gestire l'abilitazione in scrittura dei campi L-Cut Offset (simplified)
    const [isLCutOffsetEditing, setIsLCutOffsetEditing] = useState(false);

    // Al click sul save (usciamo dalla modalità editing) applichiamo subito gli offset in
    // timeline, oltre a quando si preme Apply nel drawer.
    const handleToggleLCutOffsetEdit = useCallback(() => {
        if (isLCutOffsetEditing && onOverlaysChange) {
            onOverlaysChange(applyLCutOffsetsToOverlays(overlays, lCutOffsets));
        }
        setIsLCutOffsetEditing(prev => !prev);
    }, [isLCutOffsetEditing, onOverlaysChange, overlays, lCutOffsets]);

    // Un campo per ciascun overlay di tipo REPLAY presente in timeline
    const replayOverlays = useMemo(() => {
        return (overlays || []).filter((overlay: any) => isReplaySegment(overlay));
    }, [overlays]);

    // Schema del singolo campo numerico L-Cut Offset, riutilizzato da EditorialAssetForm
    const lCutOffsetSchema: EditorialAssetRenderParameters[] = useMemo(() => [{
        name: 'lcut_offset',
        app_type: 'number',
        plat_type: 'lcut_offset',
        label: placementLabel || 'L-Cut Offset'
    }], [placementLabel]);

    const currentSelected = useMemo(() => {
        return assets.find(asset => selectedAssets.some(selected => selected.id === asset.id)) || null;
    }, [assets, selectedAssets]);

    const filteredOptions = useMemo(() => {
        return assets.filter(asset =>
            findClosestAspectRatio(asset.aspect_ratio, ASPECT_RATIO_LABELS) === aspectRatio
        );
    }, [assets, aspectRatio]);

    // Calcolati UNA SOLA VOLTA per render (dipendono solo da selectedAssets),
    // invece che dentro isAssetOptionDisabled dove venivano ricalcolati
    // per OGNI singola opzione mostrata nel menu.
    const { interleaveActive, openingActive, closerActive } = useMemo(() => {
        return {
            interleaveActive: selectedAssets.find((el: any) => el.placement_type === EDITORIAL_ASSETS.INTERLEAVE),
            openingActive: selectedAssets.find((el: any) => el.placement_type === EDITORIAL_ASSETS.OPENING_COVER),
            closerActive: selectedAssets.find((el: any) => el.placement_type === EDITORIAL_ASSETS.CLOSING_COVER),
        };
    }, [selectedAssets]);

    // useCallback: reference stabile per getOptionDisabled, così Autocomplete
    // non la considera "cambiata" ad ogni render del genitore.
    const isAssetOptionDisabled = useCallback((option: any) => {
        const pType = option?.placement_type;       
        let isDisabled = false;
        
        if([EDITORIAL_ASSETS.OPENING_COVER, EDITORIAL_ASSETS.CLOSING_COVER, EDITORIAL_ASSETS.INTERLEAVE].includes(pType)) {
            try {
                switch (pType) {
                    case EDITORIAL_ASSETS.OPENING_COVER:
                        if (interleaveActive !== undefined && interleaveActive.parameters.technical_parameters.position === 'sx' && interleaveActive.parameters.technical_parameters.margin_sx >= 0)
                            isDisabled = true;
                        break;
                    case EDITORIAL_ASSETS.CLOSING_COVER:
                        if (interleaveActive !== undefined && interleaveActive.parameters.technical_parameters.position === 'dx' && interleaveActive.parameters.technical_parameters.margin_dx >= 0)
                            isDisabled = true;
                        break;
                    case EDITORIAL_ASSETS.INTERLEAVE: {
                        const technicalParameters = option.parameters.technical_parameters;
                        if ((openingActive !== undefined && technicalParameters.position === 'sx' && technicalParameters.margin_sx >= 0) || (closerActive !== undefined && technicalParameters.position === 'dx' && technicalParameters.margin_dx >= 0))
                            isDisabled = true;
                        break;
                    }
                }
            } catch (e) { }}
        return isDisabled;
    }, [interleaveActive, openingActive, closerActive]);

    const handleAutocompleteChange = useCallback((event: any, newValue: any, reason: string) => {
        if (reason === 'selectOption' && newValue) {
            onAssetSelection(newValue);
        } else if (reason === 'clear' && currentSelected && onAssetDeselect) {
            onAssetDeselect(currentSelected);
        }
    }, [onAssetSelection, onAssetDeselect, currentSelected]);

    const isOptionEqualToValue = useCallback((option: any, value: any) => option.id === value.id, []);
    const getOptionLabel = useCallback((option: any) => option.name, []);

    const renderOption = useCallback((props: any, option: any) => {
        const { key, ...otherProps } = props;
        return (
            <Box
                component="li"
                {...otherProps}
                data-tour={`content_aggregation_asset_selector_${option.name}`}
                key={option.id || `asset_${option.name}_${Math.random()}`} // Diversificazione basata su ID
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 0.5}}
            >
                <img
                    src={option.thumbnail_path}
                    alt={option.name}
                    style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 1 }}
                />
                <Typography variant="body2" noWrap>{option.name}</Typography>
            </Box>
        );
    }, []);

    const renderInput = useCallback((params: any) => (
        <TextField
            {...params}
            label={placementLabel}
            variant="outlined"
            size="small"
            InputProps={{
                ...params.InputProps,
                startAdornment: (
                    <>
                        {currentSelected && (
                            <InputAdornment position="start" sx={{ ml: 0.5, mr: -0.5 }}>
                                <img
                                    src={currentSelected.thumbnail_path}
                                    alt={currentSelected.name}
                                    style={{
                                        width: 24,
                                        height: 22,
                                        objectFit: 'cover',
                                        borderRadius: 1
                                    }}
                                />
                            </InputAdornment>
                        )}
                        {params.InputProps.startAdornment}
                    </>
                ),
            }}
        />
    ), [placementLabel, currentSelected]);

    if (simplified) {
        if (replayOverlays.length === 0) return null;
        return (
            <Box sx={{ width: '100%' }}>
                <Box width={"100%"} display={"flex"} justifyContent={"flex-end"}>
                    <IconButton
                        onClick={handleToggleLCutOffsetEdit}
                        color="secondary"
                        disabled={disabled}
                    >
                        {isLCutOffsetEditing ? <SaveIcon /> : <EditIcon />}
                    </IconButton>
                </Box>
                {replayOverlays.map((overlay: any) => (
                    <EditorialAssetForm
                        key={'EditorialAssetFormLCut_' + overlay.id}
                        schema={lCutOffsetSchema}
                        onChange={(key: string, value: any) => setLCutOffset(String(overlay.id), String(overlay.idClip), value)}
                        defaultValues={{ lcut_offset: lCutOffsets[String(overlay.id)] ?? L_CUT_OFFSET }}
                        submitLabel={'Render Editorial asset'}
                        type={OverlayType.INTERLEAVE}
                        externalIsEditing={isLCutOffsetEditing}
                    />
                ))}
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Autocomplete
                disabled={disabled}
                options={filteredOptions}
                getOptionLabel={getOptionLabel}
                getOptionDisabled={isAssetOptionDisabled}
                value={currentSelected}
                disableClearable={!onAssetDeselect}
                onChange={handleAutocompleteChange}
                isOptionEqualToValue={isOptionEqualToValue}
                renderOption={renderOption}
                renderInput={renderInput}
            />

            <Box sx={{ mt: 1.5, width: '100%' }}>
                {currentSelected ? (
                    <Box>
                        <EditorialAssetCard
                            editorialAsset={currentSelected}
                            selected={true}
                            cardWidth={{ xs: '100%' }}
                        />
                        {displayForm && currentSelected.parameters.render_parameters && currentSelected.parameters.render_parameters.length > 0 &&
                            <Box sx={{ mt: 1, overflow: 'hidden' }}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    onClick={() => setIsFormOpen(!isFormOpen)}
                                    sx={{
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        p: 1,
                                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: 'medium' }}>
                                        Graphic Setting
                                    </Typography>
                                    <IconButton size="small" disableRipple sx={{ p: 0 }}>
                                        {isFormOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Stack>

                                {/* Contenuto Collapsible */}
                                <Collapse in={isFormOpen}>
                                    {(placementLabel.toLocaleLowerCase() !== OverlayType.INTERLEAVE) ?
                                    <Box sx={{ p: 1, pt: 0 }}>
                                        <EditorialAssetForm
                                            key={'EditorialAssetFormInterleave_' + currentSelected.id}
                                            schema={currentSelected.parameters.render_parameters}
                                            infos = {assetRenderParametersMap[currentSelected.id] ? assetRenderParametersMap[currentSelected.id].infos : {}}
                                            onChange={(key: string, value: any) => changeAssetRenderParameter(currentSelected.id, key, value)}
                                            submitLabel={'Render Editorial asset'}
                                            defaultValues={assetRenderParametersMap[currentSelected.id] ? assetRenderParametersMap[currentSelected.id].overlay_params : {}}
                                            type={OverlayType.ASSET}
                                        />
                                    </Box>
                                    :<Box sx={{ p: 1, pt: 0 }}>
                                        <Box width={"100%"} display={"flex"} justifyContent={"flex-end"}>
                                            <IconButton
                                                onClick={handleInterleaveToggleEdit}
                                                color="secondary"
                                            >
                                                {isInterleaveEditing ? <SaveIcon /> : <EditIcon />}
                                            </IconButton>
                                        </Box>
                                        {overlays &&
                                            overlays.filter((el:any) => el.type === OverlayType.ASSET && el.subtype === OverlayType.INTERLEAVE)
                                            .map((el: any, index: number) => {
                                                return <EditorialAssetForm
                                                    key={'EditorialAssetFormInterleave_'+ el.id + '_' + index}
                                                    schema={currentSelected.parameters.render_parameters}
                                                    infos={assetRenderParametersMap[currentSelected.id+'_&_'+el.id]?.infos ?? {}}
                                                    onChange={(key: string, value: any) =>
                                                        changeAssetRenderParameter(currentSelected.id+'_&_'+el.id, key, value)
                                                    }
                                                    submitLabel={'Render Editorial asset'}
                                                    defaultValues={assetRenderParametersMap[currentSelected.id+'_&_'+el.id]?.overlay_params ?? {}}
                                                    type={OverlayType.INTERLEAVE}
                                                    externalIsEditing={isInterleaveEditing}
                                                />}
                                            )
                                        }
                                    </Box>}
                                </Collapse>
                            </Box>
                        }
                    </Box>
                ) : (
                    <Box
                        sx={{
                            width: '100%',
                            aspectRatio: '16/9',
                            border: DASHED_SEPARATOR_STYLE,
                            bgcolor: 'transparent',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            No asset selected
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
});

const EditorialAssetList: React.FC<EditorialAssetListProps> = ({
        editorialAssets,
        selectedEditorialAssets,
        onAssetSelection,
        onAssetDeselect,
        layout = 'vertical',
        displayForm = false,
        aspectRatio = DEFAULT_ASPECT_RATIO,
        overlays= [],
        usageMode = AGGREGATION_USAGE_TYPE.STANDARD,
        isEditorDrawer = false,
        onOverlaysChange
    }) => {

    const isVertical = layout === 'vertical';
    const {selectedCard} = useSelectedCard();
    const {isMaterializing} = useEditorialAssets();
    const {lCut} = useMaterializationSettings();

    const { categories, typesPerCategory } = useMemo(() => {
        const tempCategories: string[] = [];
        const tempTypesPerCategory: Record<string, string[]> = {};

        // 1. Raggruppamento e popolamento
        for (const asset of editorialAssets) {
            if (!tempCategories.includes(asset.category)) {
                tempCategories.push(asset.category);
            }
            if (!tempTypesPerCategory[asset.category]) {
                tempTypesPerCategory[asset.category] = [];
            }
            if (!tempTypesPerCategory[asset.category].includes(asset.placement_type)) {
                tempTypesPerCategory[asset.category].push(asset.placement_type);
            }
        }

        // 2. Ordinamento delle categorie
        const categoryOrder = ['opening', 'mi', 'closing'];

        tempCategories.sort((a, b) => {
            const aLower = a.toLowerCase();
            const bLower = b.toLowerCase();

            const indexA = categoryOrder.findIndex(keyword => aLower.includes(keyword) || aLower === keyword);
            const indexB = categoryOrder.findIndex(keyword => bLower.includes(keyword) || bLower === keyword);

            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            return 0;
        });

        // 3. Ordinamento interno dei tipi (placement_type) per ogni categoria
        const typeOrder = ["pre", "opener", "interleave", "popup", "closing", "post"];

        Object.keys(tempTypesPerCategory).forEach(category => {
            tempTypesPerCategory[category].sort((a, b) => {
                const aLower = a.toLowerCase();
                const bLower = b.toLowerCase();

                const indexA = typeOrder.findIndex(keyword => aLower.includes(keyword) || aLower === keyword);
                const indexB = typeOrder.findIndex(keyword => bLower.includes(keyword) || bLower === keyword);

                if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                if (indexA !== -1) return -1;
                if (indexB !== -1) return 1;
                return 0;
            });
        });

        return {
            categories: tempCategories,
            typesPerCategory: tempTypesPerCategory,
        };
    }, [editorialAssets]);

    return (
        <Box sx={{ width: '100%' }}>
            <Box
                display="flex"
                flexDirection={isVertical ? 'column' : 'row'}
                gap={3}
                alignItems={isVertical ? 'stretch' : 'flex-start'}
                sx={{ width: '100%', pb:2 }}
                data-tour={categories.length > 0 ? "content_aggregation_assets_list" : "mcmcmc"}
            >
                {categories.map((assetCategory, index) => {
                    const itemsCount = typesPerCategory[assetCategory]?.length || 1;

                    // Cerchiamo un asset di questa categoria per estrapolarne la label (se presente)
                    const sampleCategoryAsset = editorialAssets.find(a => a.category === assetCategory);
                    const categoryDisplayName = sampleCategoryAsset?.category_label || assetCategory;

                    return (
                        <Box
                            key={assetCategory+'_category_'+index}
                            data-tour={`content_aggregation_${assetCategory}`}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: isVertical ? '1 1 auto' : itemsCount,
                                width: isVertical ? '100%' : '100%',
                                maxWidth: isVertical ? 'none' : `${itemsCount * 20}%`,
                                minWidth: 0
                            }}
                        >
                            <Typography
                                key={assetCategory+'_Typography_'+index}
                                variant="h6"
                                color="primary"
                                sx={{ mb: 2, borderBottom: '1px solid #eee', pb: 1,
                                    mx:(typesPerCategory[assetCategory] && typesPerCategory[assetCategory]?.length===1) ? 0.5 : 0 
                                 }}
                            >
                                {categoryDisplayName}
                            </Typography>

                            <Box
                                key={assetCategory+'_boxcategory_'+index}
                                display="flex"
                                flexDirection={isVertical ? 'column' : 'row'}
                                gap={2}
                                sx={{ width: '100%',px:(typesPerCategory[assetCategory] && typesPerCategory[assetCategory]?.length===1) ? 0.5 : 0  }}
                            >
                                {typesPerCategory[assetCategory]?.map((placement_type,i) => {
                                    // Filtriamo gli asset per questa specifica combinazione
                                    const filteredAssets = editorialAssets.filter(
                                        (asset) =>
                                            asset.category === assetCategory &&
                                            asset.placement_type === placement_type
                                    );

                                    // Estraiamo la label del placement_type dal primo asset disponibile
                                    const placementDisplayName = filteredAssets[0]?.placement_type_label || placement_type;

                                    return (
                                        <Box key={placement_type+'_category'+index+'_type'+i} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                                            <AssetSelector
                                                placementLabel={placementDisplayName}
                                                assets={filteredAssets}
                                                selectedAssets={selectedEditorialAssets}
                                                onAssetSelection={onAssetSelection}
                                                onAssetDeselect={onAssetDeselect}
                                                displayForm={displayForm}
                                                aspectRatio={aspectRatio}
                                                overlays={overlays}
                                                disabled={((selectedCard && selectedCard.length <= 0) || isMaterializing) && usageMode !== AGGREGATION_USAGE_TYPE.PRESET }
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    );
                })}
                
            </Box>
        </Box>
    );
};

export default EditorialAssetList;